import { useState, useEffect } from 'react';
import { X, AlertTriangle, DollarSign, User, Mail, Phone, Calendar, MapPin, Briefcase, Loader } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from 'axios';
import { toast } from 'sonner';

const CancelCrewMemberModal = ({ job, crewMember, crewMemberIndex, userEmail, isOpen, onClose, onSuccess, adminMode = false }) => {
  const [cancellationReason, setCancellationReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCancellationReason('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !job || !crewMember) return null;

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleConfirmCancel = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        crew_member_index: crewMemberIndex,
        hired_user_id: crewMember.userId,
        cancellation_reason: cancellationReason.trim(),
        user_email: userEmail
      };
      if (adminMode) {
        payload.admin_override = true;
        payload.charge_cancellation_fee = true;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/cancel-crew-member`,
        payload
      );

      if (response.data.success) {
        let detailMsg = '';
        if (response.data.manual_refund_required) {
          detailMsg = ' Admin has been notified to process the refund manually.';
        } else if (response.data.refund_amount > 0) {
          detailMsg = ` A refund of ${formatCurrency(response.data.refund_amount)} is being processed.`;
        }
        if (response.data.cancellation_fee_invoice_number) {
          detailMsg += ` Cancellation fee invoice ${response.data.cancellation_fee_invoice_number} issued to the crew member.`;
        }
        toast.success(`${crewMember.name} has been removed from this position.${detailMsg}`);
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Cancel crew member error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel crew member');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[200]"
      style={{ background: 'rgba(8,11,20,.7)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)', padding: 20, fontFamily: 'var(--sz-font-body)' }}
    >
      <div
        className="w-full max-w-lg flex flex-col"
        style={{
          maxHeight: '90vh',
          overflow: 'hidden',
          background: '#12162A',
          border: '1px solid rgba(185,28,28,.3)',
          borderRadius: 22,
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
        }}
      >
        {/* Header */}
        <div
          className="px-6 py-4 flex items-center justify-between flex-shrink-0"
          style={{ background: 'linear-gradient(120deg,#B91C1C,#7F1D1D)' }}
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-white" />
            <h3 style={{ fontFamily: 'var(--sz-font-display)', fontWeight: 800, fontSize: 20, color: '#fff', letterSpacing: '-.4px' }}>
              {adminMode ? 'Cancel Position (No-Show)' : 'Cancel Crew Member'}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: '0 0 auto' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Crew Member Info */}
          <div style={{ borderRadius: 12, padding: 16, background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.1)' }}>
            <h4 className="mb-3 flex items-center gap-2" style={{ fontWeight: 600, color: '#fff' }}>
              <User className="w-4 h-4" style={{ color: '#F87171' }} />
              Crew Member Being Cancelled
            </h4>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0" style={{ border: '2px solid rgba(185,28,28,.45)' }}>
                {crewMember.photo && crewMember.photo !== 'https://via.placeholder.com/150' ? (
                  <img
                    src={crewMember.photo}
                    alt={crewMember.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-bold text-xl" style={{ background: 'linear-gradient(135deg,#B91C1C,#7F1D1D)' }}>
                    {crewMember.name?.charAt(0) || '?'}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-lg font-bold" style={{ color: '#fff' }}>{crewMember.name}</p>
                <Badge className="text-xs px-2 py-0.5 mt-1" style={{ background: 'rgba(120,40,124,.25)', color: '#C98AD4' }}>
                  {crewMember.role}
                </Badge>
                {crewMember.email && (
                  <p className="text-xs mt-1 flex items-center gap-1" style={{ color: 'rgba(255,255,255,.5)' }}>
                    <Mail className="w-3 h-3" /> {crewMember.email}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm" style={{ color: 'rgba(255,255,255,.5)' }}>Position Total</p>
                <p className="text-xl font-bold" style={{ color: '#F87171', fontFamily: 'var(--sz-font-mono)' }}>{formatCurrency(crewMember.subtotal)}</p>
              </div>
            </div>
          </div>

          {/* Job Context */}
          <div style={{ borderRadius: 12, padding: 12, background: 'rgba(37,99,235,.1)', border: '1px solid rgba(37,99,235,.25)' }}>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-1">
                <Briefcase className="w-3 h-3" style={{ color: '#60A5FA' }} />
                <span className="truncate" style={{ color: 'rgba(255,255,255,.7)' }}>{job.title}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" style={{ color: '#60A5FA' }} />
                <span style={{ color: 'rgba(255,255,255,.7)' }}>
                  {job.jobDate ? new Date(job.jobDate).toLocaleDateString('en-AU', {
                    day: 'numeric', month: 'short', year: 'numeric'
                  }) : 'TBD'}
                </span>
              </div>
            </div>
          </div>

          {/* Cancellation Reason */}
          <div className="space-y-2">
            <label className="text-sm" style={{ fontWeight: 600, color: '#fff' }}>
              Reason for Cancellation <span style={{ color: '#F87171' }}>*</span>
            </label>
            <textarea
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder="Please explain why this crew member is being cancelled..."
              className="w-full resize-none text-sm"
              style={{ padding: '12px 14px', borderRadius: 11, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.14)', color: '#fff', outline: 'none', fontFamily: 'var(--sz-font-body)' }}
              rows={3}
            />
          </div>

          {/* Refund Info */}
          {adminMode ? (
            <div style={{ borderRadius: 12, padding: 16, background: 'rgba(180,83,9,.12)', border: '1px solid rgba(180,83,9,.3)' }}>
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4" style={{ color: '#FCD34D' }} />
                <span style={{ fontWeight: 600, color: '#FCD34D' }}>Manual Refund Required</span>
              </div>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,.7)' }}>
                A refund notification (around <strong style={{ color: '#fff' }}>{formatCurrency(crewMember.subtotal)}</strong>)
                will be emailed to admin to process manually via Stripe. No automatic refund is
                issued for no-show cancellations.
              </p>
            </div>
          ) : (
            <div style={{ borderRadius: 12, padding: 16, background: 'rgba(103,198,180,.1)', border: '1px solid rgba(103,198,180,.3)' }}>
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4" style={{ color: '#67C6B4' }} />
                <span style={{ fontWeight: 600, color: '#67C6B4' }}>Refund Information</span>
              </div>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,.7)' }}>
                A full refund of <strong style={{ color: '#fff' }}>{formatCurrency(crewMember.subtotal)}</strong> will be issued to the original payment method. This may take 5-10 business days to process.
              </p>
            </div>
          )}

          {/* Cancellation fee (admin no-show only) */}
          {adminMode && (
            <div style={{ borderRadius: 12, padding: 16, background: 'rgba(252,211,77,.1)', border: '1px solid rgba(252,211,77,.3)' }}>
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4" style={{ color: '#FCD34D' }} />
                <span style={{ fontWeight: 600, color: '#FCD34D' }}>Cancellation Fee Invoice</span>
              </div>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,.7)' }}>
                A cancellation fee invoice (7% of position total + 10% GST) will be issued to{' '}
                <strong style={{ color: '#fff' }}>{crewMember.name}</strong> as the cancelling party. They will receive an
                email with payment instructions.
              </p>
            </div>
          )}

          {/* Warning */}
          <div style={{ borderRadius: 12, padding: 16, background: 'rgba(185,28,28,.1)', border: '1px solid rgba(185,28,28,.4)' }}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#F87171' }} />
              <div>
                <p style={{ fontWeight: 600, color: '#F87171' }}>This action cannot be undone</p>
                <ul className="text-sm mt-1 list-disc list-inside" style={{ color: 'rgba(255,255,255,.7)' }}>
                  <li>This crew member will be removed from the job</li>
                  {adminMode ? (
                    <>
                      <li>Admin will be notified to refund the hirer manually via Stripe</li>
                      <li>A cancellation fee invoice will be issued to the crew member</li>
                    </>
                  ) : (
                    <li>A refund will be processed automatically</li>
                  )}
                  <li>The position will reopen for a new hire</li>
                  <li>Other hired crew members will not be affected</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div
          className="p-4 flex-shrink-0 flex items-center justify-between"
          style={{ borderTop: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.02)' }}
        >
          <Button
            onClick={onClose}
            variant="outline"
            disabled={isSubmitting}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#fff' }}
          >
            Go Back
          </Button>
          <Button
            onClick={handleConfirmCancel}
            disabled={!cancellationReason.trim() || isSubmitting}
            style={{ background: '#B91C1C', color: '#fff', border: 'none' }}
          >
            {isSubmitting ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 mr-2" />
                {adminMode ? 'Cancel Position (No-Show)' : 'Cancel Crew Member'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CancelCrewMemberModal;
